import { ReactNode } from 'react';
import Popover from '@amzn/awsui-components-react/polaris/popover';
import KeyValuePairs from '@amzn/awsui-components-react/polaris/key-value-pairs';
import { Event, Shift } from '@amzn/red-velvet-api';
import {
  doZonesDifferAtInstant,
  formatShiftTime,
  formatShiftTimeRangeWithZone,
  resolveViewerZone,
  toDate,
} from '../../utils/dateFormatting';
import { useLocalizedText } from '../../hooks/localizedText';
import { useLocale } from '../../hooks/locale';
import { useTimezone } from '../../hooks/timezone';
import { resolveEventDisplayZone, resolveLocationZone } from '../../utils/eventUtils';

/**
 * The popover copy and the "Ongoing" fallback live in the shifts bundle under this root, where the
 * volunteer shift table first shipped them. The coordinator shift table renders the same cell and
 * reads the same keys, so these strings are translated once rather than duplicated per surface.
 */
const SHIFT_TIME_CELL_I18N = 'volunteerEventShiftsTable';
const SHIFT_TIME_CELL_NAMESPACES = ['shifts', 'translation'];

/**
 * Resolves the IANA zone a shift's date and time should render in, so the Date and Time columns
 * agree on the calendar day near a timezone day boundary. Returns both the location-anchored
 * `shiftZone` (undefined when there is none) and the `displayZone` the cells format in: callers that
 * only render a value use `displayZone`, while the Time cell also needs `shiftZone` to decide its
 * viewer/event mismatch popover.
 */
export function resolveShiftDisplayZone(
  event: Event,
  shift: Shift,
  viewerZone: string
): { shiftZone: string | undefined; displayZone: string } {
  // The shift's effective location: its own only when it opts out of the event location, otherwise
  // the event's (mirrors getShiftLocationLabel so the Time and Location cells agree on the location).
  // A shift on the event location resolves exactly as the event header does, so the date a viewer
  // reads in the header always matches the shift rows under it.
  if (shift.useEventLocation !== false) {
    const { eventZone, displayZone } = resolveEventDisplayZone(event, viewerZone);
    return { shiftZone: eventZone, displayZone };
  }

  // A shift with its own location still uses event.timeZone: the Location model carries no per-shift
  // timezone, so the event's stored zone is the only location-anchored zone available (its
  // reliability is bounded by the upstream geocoding bug, SIM b14a82cc). resolveLocationZone drops it
  // for a virtual shift or an invalid zone, which then renders in the viewer's zone with no popover
  // for BOTH the cell text and the mismatch decision.
  const shiftZone = resolveLocationZone(shift.location, event.timeZone);
  return { shiftZone, displayZone: shiftZone ?? viewerZone };
}

/**
 * The shared Time-column cell for the volunteer and coordinator shift tables. A virtual shift (or an
 * in-person shift with no usable event zone) renders in the viewer's local zone with no popover; an
 * in-person shift renders in the event's zone and, when that zone sits at a different UTC offset than
 * the viewer's at the shift instant, offers a popover contrasting the event-local and viewer-local
 * times. `emptyDisplay` is the fallback for a shift with no start time; the two tables pass different
 * "Ongoing" strings, so it defaults to the shifts-bundle label when a caller omits it.
 */
export function ShiftTimeCell({
  event,
  shift,
  emptyDisplay,
}: {
  event: Event;
  shift: Shift;
  emptyDisplay?: ReactNode;
}) {
  const { t } = useLocalizedText(SHIFT_TIME_CELL_NAMESPACES);
  const locale = useLocale();
  const rawViewerZone = useTimezone();
  const viewerZone = resolveViewerZone(rawViewerZone);

  // shiftZone (the location-anchored zone, undefined when it falls back) is still needed here, beyond
  // displayZone, to decide the viewer/event mismatch popover below.
  const { shiftZone, displayZone } = resolveShiftDisplayZone(event, shift, viewerZone);

  const start = formatShiftTime(shift.startDateTime, displayZone, locale);
  const end = formatShiftTime(shift.endDateTime, displayZone, locale);

  const timeText = start && end ? `${start} - ${end}` : start;
  if (!timeText) return <>{emptyDisplay ?? t(`${SHIFT_TIME_CELL_I18N}.ongoing`)}</>;

  // Coerce the shift start to a Date for the offset comparison (Timestamp is a Date at runtime; the
  // string branch is defensive). The mismatch is decided at the shift instant, so DST is respected.
  const startDate = toDate(shift.startDateTime);

  // Flag a mismatch only for a physical shift whose zone sits at a DIFFERENT UTC offset than the
  // viewer's AT the shift instant — an offset comparison, not IANA string equality, so two zones that
  // share an offset at that instant (e.g. Europe/London and UTC in winter) never trigger the popover.
  // The mismatch is judged at the shift START instant, so a shift that crosses a DST boundary is
  // classified on its start offset.
  const showMismatch =
    shiftZone !== undefined && doZonesDifferAtInstant(startDate, shiftZone, viewerZone);
  if (!showMismatch) return <>{timeText}</>;

  return (
    <Popover
      header={t(`${SHIFT_TIME_CELL_I18N}.timezoneMismatch.header`)}
      triggerType="text"
      size="large"
      content={
        <KeyValuePairs
          columns={2}
          items={[
            {
              label: t(`${SHIFT_TIME_CELL_I18N}.timezoneMismatch.eventLocalTime`),
              value: formatShiftTimeRangeWithZone(
                startDate,
                toDate(shift.endDateTime),
                shiftZone,
                locale
              ),
            },
            {
              label: t(`${SHIFT_TIME_CELL_I18N}.timezoneMismatch.yourLocalTime`),
              value: formatShiftTimeRangeWithZone(
                startDate,
                toDate(shift.endDateTime),
                viewerZone,
                locale
              ),
            },
          ]}
        />
      }
    >
      {timeText}
    </Popover>
  );
}
